import {
  LEAGUE_STEPS,
  LP_PER_DIVISION,
  xpToReachNext,
  type AccountLevelSnapshot,
  type DuelXpResult,
  type LeagueDivision,
  type LeagueId,
  type RankedDelta,
  type RankedStanding,
} from '@shared/index'

const MIN_SEGMENT_MS = 360
const MAX_SEGMENT_MS = 900
const HOLD_MS = 280

/** King of Games is the last ladder step and has no division cap. */
export const KING_STEP = LEAGUE_STEPS.length - 1

/**
 * One sweep of the bar. A duel that promotes or levels up needs two: fill to
 * the top, then start over on the next step.
 */
export type BarSegment = {
  from: number
  to: number
  /** Bar width for this sweep (LP per division, or XP needed this level). */
  max: number
  /** Ladder step or account level the bar shows while this sweep runs. */
  step: number
}

export type BarTween = {
  /** Jump to the final frame. */
  skip: () => void
  cancel: () => void
}

export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

function segmentMs(segment: BarSegment): number {
  const span =
    segment.max > 0 ? Math.abs(segment.to - segment.from) / segment.max : 1
  const ms =
    MIN_SEGMENT_MS + (MAX_SEGMENT_MS - MIN_SEGMENT_MS) * Math.min(1, span)
  return Math.round(ms)
}

/**
 * Runs the segments back to back, holding briefly on each step change.
 * `onFrame` gets the live value; `onDone` fires once, also when skipped.
 */
export function tweenSegments(
  segments: readonly BarSegment[],
  onFrame: (value: number, segment: BarSegment, index: number) => void,
  onDone: () => void,
): BarTween {
  const last = segments[segments.length - 1]
  let finished = false
  let raf = 0
  let timer = 0

  const finish = (): void => {
    if (finished) return
    finished = true
    window.cancelAnimationFrame(raf)
    window.clearTimeout(timer)
    if (last) onFrame(last.to, last, segments.length - 1)
    onDone()
  }

  if (segments.length === 0 || prefersReducedMotion()) {
    finish()
    return { skip: finish, cancel: finish }
  }

  const runSegment = (index: number): void => {
    const segment = segments[index]
    if (!segment) {
      finish()
      return
    }
    const duration = segmentMs(segment)
    const startedAt = performance.now()
    const tick = (now: number): void => {
      if (finished) return
      const t = Math.min(1, (now - startedAt) / duration)
      onFrame(
        segment.from + (segment.to - segment.from) * easeOut(t),
        segment,
        index,
      )
      if (t < 1) {
        raf = window.requestAnimationFrame(tick)
        return
      }
      if (index + 1 >= segments.length) {
        finish()
        return
      }
      timer = window.setTimeout(() => runSegment(index + 1), HOLD_MS)
    }
    onFrame(segment.from, segment, index)
    raf = window.requestAnimationFrame(tick)
  }

  runSegment(0)

  return {
    skip: finish,
    cancel: () => {
      finished = true
      window.cancelAnimationFrame(raf)
      window.clearTimeout(timer)
    },
  }
}

/**
 * Index of a league + division pair in `LEAGUE_STEPS`.
 */
export function ladderStepIndex(
  league: LeagueId | null,
  division: LeagueDivision | null,
): number {
  if (!league) return 0
  const i = LEAGUE_STEPS.findIndex(
    (step) => step.league === league && step.division === division,
  )
  return Math.max(0, i)
}

/**
 * Standing before the duel. Older servers omit it, so fall back to undoing
 * the LP delta — exact except right on a promotion or demotion.
 */
export function standingBefore(after: RankedDelta): RankedStanding {
  if (after.before) return after.before
  return {
    league: after.league,
    division: after.division,
    lp: Math.min(LP_PER_DIVISION - 1, Math.max(0, after.lp - after.lpDelta)),
    placementRemaining: after.placementRemaining,
  }
}

/**
 * LP sweeps between two standings. Empty while the account is still placing.
 */
export function buildLpSegments(
  before: RankedStanding,
  after: RankedDelta,
): BarSegment[] {
  if (!after.league || after.placementRemaining > 0) return []

  const toStep = ladderStepIndex(after.league, after.division)
  const fromStep = before.league
    ? ladderStepIndex(before.league, before.division)
    : toStep
  const fromLp = before.league ? before.lp : 0

  if (fromStep === toStep) {
    return [{ from: fromLp, to: after.lp, max: LP_PER_DIVISION, step: toStep }]
  }
  if (fromStep < toStep) {
    return [
      {
        from: fromLp,
        to: LP_PER_DIVISION,
        max: LP_PER_DIVISION,
        step: fromStep,
      },
      { from: 0, to: after.lp, max: LP_PER_DIVISION, step: toStep },
    ]
  }
  return [
    { from: fromLp, to: 0, max: LP_PER_DIVISION, step: fromStep },
    {
      from: LP_PER_DIVISION,
      to: after.lp,
      max: LP_PER_DIVISION,
      step: toStep,
    },
  ]
}

/**
 * XP sweeps between two account snapshots, one per level crossed.
 */
export function buildXpSegments(
  before: AccountLevelSnapshot,
  after: DuelXpResult,
): BarSegment[] {
  if (after.level <= before.level) {
    return [
      {
        from: before.xpIntoLevel,
        to: after.xpIntoLevel,
        max: after.xpToNext,
        step: after.level,
      },
    ]
  }

  const segments: BarSegment[] = []
  for (let level = before.level; level < after.level; level++) {
    const max = xpToReachNext(level)
    segments.push({
      from: level === before.level ? before.xpIntoLevel : 0,
      to: max,
      max,
      step: level,
    })
  }
  segments.push({
    from: 0,
    to: after.xpIntoLevel,
    max: after.xpToNext,
    step: after.level,
  })
  return segments
}
